import { useAuth0 } from "@auth0/auth0-react";
import { Avatar } from "@mui/material";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";

import configuration from "@/configuration";
import { CommentsService } from "@/services/admin-services";
import instance from "@/services/axiosConfig";
import { LocaleService } from "@/services/global-services";
import { ContentService, ElementService } from "@/services/user-services";
import { useDataStore, useFilterStore, useLocaleStore } from "@/stores";
import { PencilIcon } from "@heroicons/react/24/outline";
import CommentIcon from "@mui/icons-material/Comment";
import DashboardIcon from "@mui/icons-material/Dashboard";
import clsx from "clsx";
import { Link, useParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";

const AdminBar = () => {
    const activeRegion = useLocaleStore(useShallow((state) => state.activeRegion));
    const activeLanguage = useLocaleStore(useShallow((state) => state.activeLanguage));

    const activeVersion = useFilterStore((state) => state.activeVersion);
    const setActiveVersion = useFilterStore((state) => state.setActiveVersion);
    const versions = useDataStore((state) => state.versions);
    const previewVersion = useDataStore((state) => state.previewVersion);
    const setPreviewVersion = useDataStore((state) => state.setPreviewVersion);
    const setDisciplines = useDataStore((state) => state.setDisciplines);
    const setPhases = useDataStore((state) => state.setPhases);
    const setSoftware = useDataStore((state) => state.setSoftware);
    const setUseCases = useDataStore((state) => state.setUseCases);
    const setElements = useDataStore((state) => state.setElements);
    const pages = useDataStore((state) => state.pages);
    const setPages = useDataStore((state) => state.setPages);
    const elements = useDataStore((state) => state.elements);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { user, getAccessTokenSilently, loginWithRedirect } = useAuth0();
    const interceptorRef = useRef<number | null>(null);

    const commentsService = useMemo(() => new CommentsService("int"), []);
    const [openComments, setOpenComments] = useState<number>(0);

    const localeService = useMemo(
        () => new LocaleService(activeRegion!, activeLanguage!),
        [activeRegion, activeLanguage]
    );

    const contentService = useMemo(
        () => new ContentService(activeRegion!, activeLanguage!),
        [activeRegion, activeLanguage]
    );

    const elementService = useMemo(
        () => new ElementService(activeRegion!, activeLanguage!),
        [activeRegion, activeLanguage]
    );

    const { "*": slug } = useParams();
    const currentPageId = pages.find((page) => page.url === slug)?.id;
    const currentElementId = elements[activeVersion!]?.find((element) => slug?.endsWith(element.slug))?.elementId;

    useEffect(() => {
        if (previewVersion) {
            setActiveVersion("wip");
            setPreviewVersion(true);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        interceptorRef.current = instance.interceptors.request.use(
            async (config) => {
                try {
                    if (config.url?.startsWith(configuration.assets)) return config;

                    const accessToken = await getAccessTokenSilently();
                    config.headers["Authorization"] = `Bearer ${accessToken}`;

                    return config;
                } catch {
                    loginWithRedirect({
                        authorizationParams: {
                            audience: configuration.api,
                            scope: configuration.auth.scope,
                            redirect_uri: `${window.location.origin}/admin`,
                        },
                        appState: { returnTo: window.location.pathname },
                    });
                }

                return config;
            },
            () => {
                loginWithRedirect({
                    authorizationParams: {
                        audience: configuration.api,
                        scope: configuration.auth.scope,
                        redirect_uri: `${window.location.origin}/admin`,
                    },
                    appState: { returnTo: window.location.pathname },
                });

                return;
            }
        );

        return () => {
            if (!interceptorRef.current) return;
            instance.interceptors.request.eject(interceptorRef.current);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        commentsService.getElementComments().then((comments) => {
            setOpenComments(comments.reduce((total, comment) => total + comment.unreadComments, 0));
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setPreviewVersion(activeVersion === "wip");

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeVersion]);

    useEffect(() => {
        if (previewVersion) fetchWipData();
        else {
            const defaultVersion = versions.find((x) => x.isDefault)!;
            setActiveVersion(activeVersion === "wip" ? defaultVersion.id : activeVersion);
            setPages(pages.filter((page) => page.published));
            elementService.get(defaultVersion.name).then((elements) => setElements(defaultVersion.id, elements));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewVersion]);

    const onTogglePreview = (event: ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        setPreviewVersion(checked);
        if (checked) setActiveVersion("wip");
    };

    const fetchWipData = async () => {
        setIsLoading(true);

        const pagesPromise = contentService.getPages().then(setPages);
        const elementsPromise = elementService.get("wip").then((elements) => setElements("wip", elements));

        const activeVersionPromises = [];

        const softwarePromise = localeService
            .getSoftware("wip")
            .then((software) => setSoftware("wip", software))
            .catch(() => setSoftware("wip", []));

        const disciplinesPromise = localeService
            .getDisciplines("wip")
            .then((disciplines) => setDisciplines("wip", disciplines))
            .catch(() => setDisciplines("wip", []));

        const phasesPromise = localeService
            .getPhases("wip")
            .then((phases) => setPhases("wip", phases))
            .catch(() => setPhases("wip", []));

        const useCasesPromise = localeService
            .getUseCases("wip")
            .then((useCases) => setUseCases("wip", useCases))
            .catch(() => setUseCases("wip", []));

        activeVersionPromises.push(softwarePromise, disciplinesPromise, phasesPromise, useCasesPromise);

        await Promise.all([pagesPromise, elementsPromise, ...activeVersionPromises]);

        setIsLoading(false);
    };

    return (
        <div id="top-bar" className="grid center items-center py-2 gap-x-8 text-sm border-b border-zinc-300">
            <div className="grid grid-cols-3 container mx-auto 2xl:px-[108px]">
                <div className="flex items-center gap-2">
                    <Link
                        to="/admin"
                        className="flex text-white font-medium text-sm/normal items-center px-3 py-2 bg-primary-500 hover:bg-primary-600 rounded-lg disabled:opacity-50 h-10"
                    >
                        <DashboardIcon className="w-5 h-5 mr-2" />
                        Administration
                    </Link>
                    <Link
                        to="/admin/page-comments"
                        className="flex text-white font-medium text-sm/normal items-center px-3 py-2 bg-primary-500 hover:bg-primary-600 rounded-lg mr-auto disabled:opacity-50 h-10"
                    >
                        <CommentIcon className="w-5 h-5 mr-2" />
                        {openComments}
                    </Link>
                </div>

                <div className="grid grid-cols-[1fr_auto_1fr] h-fit self-center">
                    <span className="text-xs text-right mr-2 content-center">Live</span>
                    <div
                        className={clsx([
                            "group relative cursor-pointer inline-flex w-11 shrink-0 rounded-full p-0.5 inset-ring inset-ring-gray-900/5 outline-offset-2 outline-primary-600 transition-colors duration-200 ease-in-out  has-focus-visible:outline-2",
                            isLoading ? "bg-gray-100" : "bg-gray-200 has-checked:bg-primary-600",
                        ])}
                    >
                        <span className="relative size-5 rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out group-has-checked:translate-x-5">
                            <span
                                aria-hidden="true"
                                className="absolute inset-0 flex size-full items-center justify-center opacity-100 transition-opacity duration-200 ease-in group-has-checked:opacity-0 group-has-checked:duration-100 group-has-checked:ease-out"
                            >
                                <svg fill="none" viewBox="0 0 12 12" className="size-3 text-gray-400">
                                    <path
                                        d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </span>
                            <span
                                aria-hidden="true"
                                className="absolute inset-0 flex size-full items-center justify-center opacity-0 transition-opacity duration-100 ease-out group-has-checked:opacity-100 group-has-checked:duration-200 group-has-checked:ease-in"
                            >
                                {isLoading ? (
                                    <svg
                                        className="animate-spin h-5 w-5 text-primary-500"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                    </svg>
                                ) : (
                                    <svg fill="currentColor" viewBox="0 0 12 12" className="size-3 text-primary-600">
                                        <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                                    </svg>
                                )}
                            </span>
                        </span>
                        <input
                            name="preview"
                            type="checkbox"
                            aria-label="Toggle preview mode"
                            className="absolute inset-0 appearance-none focus:outline-hidden"
                            checked={previewVersion}
                            onChange={onTogglePreview}
                            disabled={isLoading}
                        />
                    </div>
                    <span className="text-xs ml-2 content-center">Preview</span>
                </div>

                <div className="flex items-center gap-2">
                    <Link
                        to={
                            currentPageId
                                ? `/admin/pages/${currentPageId}`
                                : currentElementId
                                  ? `/admin/database/${currentElementId}/int-${activeLanguage}`
                                  : ""
                        }
                        target="_blank"
                        className={clsx([
                            "flex text-black font-medium text-sm/normal items-center px-3 py-2 bg-zinc-100 hover:bg-zinc-200 rounded-lg ml-auto disabled:opacity-50 h-10",
                            !currentPageId && !currentElementId && "opacity-50 pointer-events-none",
                        ])}
                    >
                        <PencilIcon className="w-5 h-5 mr-2" />
                        Edit current page
                    </Link>

                    <Avatar alt={user?.name} src={user?.picture} />
                </div>
            </div>
        </div>
    );
};

export default AdminBar;
